import React from 'react';
import { useSelector } from 'react-redux';
import {
  TierMap,
  RewardSeedRequirement,
  getCurrentCycle,
  getNextCycle,
} from 'entity/reward/rewardTypes';
import dayjs from 'dayjs';
import { RootState } from 'reducer';
import TierColorContainer from 'components/reward/TierColorContainer';
import clsx from 'clsx';

const generateCycleText = (tier: number, seeds: number) => {
  const currentCycle = getCurrentCycle();
  const nextCycle = getNextCycle();
  const currCylceEndDate = dayjs(currentCycle[1]).format('DD MMM YYYY');
  const nextCylceEndDate = dayjs(nextCycle[1]).format('DD MMM YYYY');
  if (tier === 1) {
    const require = RewardSeedRequirement[2] - seeds;
    return `Earn ${require?.toLocaleString()} seeds by ${currCylceEndDate} to unlock Billionaire!`;
  }

  if (tier === 2) {
    if (seeds <= RewardSeedRequirement[2]) {
      const require = RewardSeedRequirement[2] - seeds;
      return `Earn ${require?.toLocaleString()} seeds by ${currCylceEndDate} to remain in Billionaire!`;
    }
    const require = RewardSeedRequirement[3] - seeds;
    return `Earn ${require?.toLocaleString()} seeds by ${currCylceEndDate} to unlock Trillionaire!`;
  }

  if (tier === 3) {
    if (seeds <= RewardSeedRequirement[3]) {
      const require = RewardSeedRequirement[3] - seeds;
      return `Earn  ${require?.toLocaleString()} seeds by ${currCylceEndDate} to remain in Trillionaire!`;
    }
    return `Yay! Your Trillionaire status will remain till ${nextCylceEndDate}.`;
  }

  return '';
};

const TierProgressCard = () => {
  const profile = useSelector((state: RootState) => state.profile.user);
  const tier = profile?.tier || 1;
  const currentCycleSeeds = profile?.currentCycleSeeds || 0;
  const currentCycle = getCurrentCycle();
  const startCycle = dayjs(currentCycle[0]).format('DD MMM');
  const endCycle = dayjs(currentCycle[1]).format('DD MMM YYYY');
  const nextLevelSeeds =
    RewardSeedRequirement[tier + 1] || RewardSeedRequirement[3];
  const percentage = (currentCycleSeeds / nextLevelSeeds) * 100;

  return (
    <div className="w-full pl-4 pr-4 pt-4 lg:pl-0 lg:pr-0 lg:pt-0">
      <TierColorContainer
        tier={tier}
        className="rounded-lg p-4 w-full rounded-b-none"
      >
        <div className="flex flex-col gap-1">
          <p className="text-sm font-bold text-white">YOUR TIER PROGRESS</p>
          <p className="text-white">
            Current tier cycle: {startCycle} - {endCycle}
          </p>
        </div>
      </TierColorContainer>
      <div className="rounded-lg p-4 w-full rounded-t-none bg-white py-[10px] px-4">
        <div className="flex flex-col gap-2 w-full">
          <div className="flex justify-between items-center w-full">
            <p
              className={clsx(
                'lg:text-[32px] text-2xl font-bold leading-[44px]',
                {
                  'text-purple-200': tier === 0 || tier === 1,
                  'text-orange-100': tier === 2,
                  'text-blue-500': tier === 3,
                },
              )}
            >
              {TierMap[tier]}
            </p>
            <div className="flex flex-col items-end">
              <p
                className={clsx('text-2xl font-bold', {
                  'text-purple-200': tier === 0 || tier === 1,
                  'text-orange-100': tier === 2,
                  'text-blue-500': tier === 3,
                })}
              >
                {currentCycleSeeds?.toLocaleString()}
              </p>
              <p className="text-[10px] lg:text-xs font-bold text-right text-neutral-500">
                SEEDS EARNED THIS CYCLE
              </p>
            </div>
          </div>
          <div className="rounded-lg overflow-hidden relative h-1 bg-neutral-300 w-fu'">
            <div
              className={clsx('absolute top-0 left-0 h-1 rounded-lg', {
                'bg-purple-200': tier === 0 || tier === 1,
                'bg-orange-100': tier === 2,
                'bg-blue-500': tier === 3,
              })}
              style={{ width: `${percentage > 100 ? 100 : percentage}%` }}
            />
          </div>
          <p className="text-xs lg:text-sm text-neutral-500">
            {generateCycleText(tier, currentCycleSeeds)}
          </p>
        </div>
      </div>
    </div>
  );
};

export default TierProgressCard;
